import { useEffect, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, mediaApi, type MediaAsset, type PostType, type PostUserTag } from '../../lib/api';
import { Icon } from '../../components/icons';
import { Button, Field, IconButton, Modal, Textarea, buttonClasses } from '../../components/ui';
import i18n from '../../i18n';
import { usePlanBlockReporter } from '../billing/plan';
import { errorMessage } from '../contacts/shared';
import { INSTAGRAM_ONLY, POST_LIMITS, type PostTargets } from './limits';
import { prepareMedia, type MediaPurpose } from './prepareMedia';
import { TagPeopleEditor } from './TagPeopleEditor';

export interface DraftItem {
  key: string;
  asset: MediaAsset;
  userTags: PostUserTag[];
  altText: string;
}

interface Upload {
  id: number;
  name: string;
  progress: number;
  error: string | null;
}

const ACCEPT: Record<PostType, string> = {
  image: 'image/*',
  carousel: 'image/*,video/mp4,video/quicktime',
  reel: 'video/mp4,video/quicktime',
  story: 'image/*,video/mp4,video/quicktime',
};

const VIDEO_TYPES: ReadonlySet<string> = new Set(['video/mp4', 'video/quicktime']);

/** A multi-photo post on a Page holds photos only (D-143). */
const photosOnly = (type: PostType, targets: PostTargets) => type === 'image' || (type === 'carousel' && targets.facebook);

function accepts(type: PostType, targets: PostTargets, file: File): boolean {
  const isImage = file.type.startsWith('image/');
  const isVideo = VIDEO_TYPES.has(file.type);
  return photosOnly(type, targets) ? isImage : type === 'reel' ? isVideo : isImage || isVideo;
}

/** How the file is prepared for the format (D-092, D-136): feed photos are cropped, the rest shown as they are. */
const PURPOSE: Record<PostType, MediaPurpose> = { image: 'feed', carousel: 'feed', reel: 'reel', story: 'story' };

/**
 * The dropzone's lines, empty and with media already in, and the media rules
 * spelled out (R16, R24). A post that reaches a Page (D-143) says its rules:
 * 4 MB photos, no videos among several photos, shorter Reels.
 */
function dropCopy(type: PostType, targets: PostTargets): { empty: string; more: string; hint: string } {
  const t = i18n.t.bind(i18n);
  const toPage = targets.facebook;
  const both = targets.facebook && targets.instagram;
  switch (type) {
    case 'image':
      return {
        empty: t('composerMedia.drop.image.empty'),
        more: t('composerMedia.drop.image.more'),
        hint: t(both ? 'composerMedia.drop.image.hintBoth' : toPage ? 'composerMedia.drop.image.hintPage' : 'composerMedia.drop.image.hint'),
      };
    case 'carousel':
      return {
        empty: t('composerMedia.drop.carousel.empty'),
        more: t(toPage ? 'composerMedia.drop.carousel.morePage' : 'composerMedia.drop.carousel.more'),
        hint: t(both ? 'composerMedia.drop.carousel.hintBoth' : toPage ? 'composerMedia.drop.carousel.hintPage' : 'composerMedia.drop.carousel.hint'),
      };
    case 'reel':
      return {
        empty: t('composerMedia.drop.reel.empty'),
        more: t('composerMedia.drop.reel.more'),
        hint: t(toPage ? 'composerMedia.drop.reel.hintPage' : 'composerMedia.drop.reel.hint'),
      };
    default:
      return { empty: t('composerMedia.drop.story.empty'), more: t('composerMedia.drop.story.more'), hint: t('composerMedia.drop.story.hint') };
  }
}

const BADGE = 'absolute top-2 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-semibold text-white';
const TILES = 'grid grid-cols-[repeat(auto-fill,minmax(128px,1fr))] gap-3';

let uploadSeq = 0;

export function MediaThumb({ asset, className = '' }: { asset: MediaAsset; className?: string }) {
  return asset.kind === 'video' ? (
    <video src={`${asset.url}#t=0.1`} muted preload="metadata" className={`object-cover ${className}`} />
  ) : (
    <img src={asset.url} alt="" className={`object-cover ${className}`} />
  );
}

const uploadError = (err: unknown) => (err instanceof ApiError || !(err instanceof Error) ? errorMessage(err) : err.message);

export function MediaPanel({
  type,
  items,
  onItemsChange,
  cover,
  onCoverChange,
  onBusyChange,
  targets = INSTAGRAM_ONLY,
}: {
  type: PostType;
  items: DraftItem[];
  onItemsChange: (items: DraftItem[]) => void;
  cover: MediaAsset | null;
  onCoverChange: (cover: MediaAsset | null) => void;
  onBusyChange: (busy: boolean) => void;
  /** Where the post goes (D-143): a Page takes no people tags, no cover, no video among several photos. */
  targets?: PostTargets;
}) {
  const { t } = useTranslation();
  const reportPlanBlock = usePlanBlockReporter();
  const fileInput = useRef<HTMLInputElement>(null);
  const coverInput = useRef<HTMLInputElement>(null);
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [notes, setNotes] = useState<string[]>([]);
  // The last four, each once: the same file dropped twice is one note.
  const addNotes = (next: string[]) => setNotes((all) => [...new Set([...all.filter((n) => !next.includes(n)), ...next])].slice(-4));
  const [editing, setEditing] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  // Uploads finish in any order; appends go through the latest list, not a stale prop.
  const itemsRef = useRef(items);
  itemsRef.current = items;
  // The format can change while files upload; read it when each one lands.
  const typeRef = useRef(type);
  typeRef.current = type;

  // Busy while any upload is still running — derived, so overlapping batches
  // (more files, a cover) cannot clear it early.
  useEffect(() => {
    onBusyChange(uploads.some((u) => u.error === null));
  }, [uploads, onBusyChange]);

  const isCarousel = type === 'carousel';
  const maxItems = isCarousel ? POST_LIMITS.carouselMax : 1;
  const pendingCount = uploads.filter((u) => u.error === null).length;

  const updateUpload = (id: number, patch: Partial<Upload>) =>
    setUploads((all) => all.map((u) => (u.id === id ? { ...u, ...patch } : u)));

  const upload = async (file: File, purpose: MediaPurpose): Promise<MediaAsset | null> => {
    uploadSeq += 1;
    const id = uploadSeq;
    setUploads((all) => [...all, { id, name: file.name, progress: 0, error: null }]);
    try {
      const prepared = await prepareMedia(file, purpose, targets);
      if (prepared.note) addNotes([t('composerMedia.note', { name: file.name, note: prepared.note })]);
      const asset = await mediaApi.upload(prepared.blob, prepared.filename, (progress) => updateUpload(id, { progress }));
      setUploads((all) => all.filter((u) => u.id !== id));
      return asset;
    } catch (err) {
      // The plan's media storage is full (402): the shared toast says so, with "Changer d'offre" (D-183).
      if (reportPlanBlock(err)) setUploads((all) => all.filter((u) => u.id !== id));
      else updateUpload(id, { error: uploadError(err) });
      return null;
    }
  };

  const addFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    return addFileList(files);
  };

  // A drop is not filtered by the input's `accept`: what Instagram would refuse is named and skipped.
  const dropFiles = (files: File[]) => {
    const refused = files.filter((file) => !accepts(type, targets, file));
    const reason = t(photosOnly(type, targets) ? 'composerMedia.refused.image' : `composerMedia.refused.${type}`);
    if (refused.length > 0) addNotes(refused.map((file) => t('composerMedia.note', { name: file.name, note: reason })));
    return addFileList(files.filter((file) => accepts(type, targets, file)));
  };

  const addFileList = async (files: File[]) => {
    if (files.length === 0) return;
    const room = isCarousel ? Math.max(0, maxItems - items.length - pendingCount) : 1;
    const skipped = files.length - room;
    if (skipped > 0) {
      addNotes([isCarousel ? t('composerMedia.skipped', { count: skipped, max: maxItems }) : t('composerMedia.onlyOne', { name: files[0]!.name })]);
    }
    const purpose = PURPOSE[type];
    await Promise.all(
      files.slice(0, room).map(async (file) => {
        const asset = await upload(file, purpose);
        if (!asset) return;
        const item: DraftItem = { key: asset.id, asset, userTags: [], altText: '' };
        // Single-media formats replace; a carousel appends in finishing order.
        const next = typeRef.current === 'carousel' ? [...itemsRef.current, item] : [item];
        itemsRef.current = next;
        onItemsChange(next);
      }),
    );
  };

  const addCover = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const asset = await upload(file, 'cover');
    if (asset) onCoverChange(asset);
  };

  const move = (index: number, delta: number) => {
    const next = [...items];
    const [moved] = next.splice(index, 1);
    if (!moved) return;
    next.splice(index + delta, 0, moved);
    onItemsChange(next);
  };
  const patchItem = (key: string, patch: Partial<DraftItem>) =>
    onItemsChange(items.map((item) => (item.key === key ? { ...item, ...patch } : item)));
  const removeItem = (key: string) => onItemsChange(items.filter((i) => i.key !== key));
  const editingItem = items.find((item) => item.key === editing) ?? null;
  const canAdd = isCarousel ? items.length + pendingCount < maxItems : true;
  // People are tagged on feed photos only: a Reel points to collaborators, a story carries nothing (D-136).
  // Feed photos always take alt text; people tags are Instagram's (D-143).
  const canDescribe = type === 'image' || isCarousel;
  const canTag = canDescribe && targets.instagram;
  const copy = dropCopy(type, targets);
  const hasMedia = items.length > 0 || uploads.length > 0;
  const onDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsDragging(false);
    void dropFiles(Array.from(event.dataTransfer.files));
  };
  const taggedBadge = (item: DraftItem) =>
    item.userTags.length > 0 ? <span className={`${BADGE} right-2`}>{t('composerMedia.tagged', { count: item.userTags.length })}</span> : null;

  const uploadTiles = uploads.map((u) => (
    <li key={u.id} className="flex aspect-square min-w-0 flex-col justify-end rounded-xl bg-surface-2 p-3 ring-1 ring-border">
      <p className="truncate text-xs font-medium text-ink-2">{u.name}</p>
      {u.error ? (
        <>
          <p className="mt-1 text-xs text-danger-700">{u.error}</p>
          <button
            type="button"
            onClick={() => setUploads((all) => all.filter((x) => x.id !== u.id))}
            className="touch-target mt-1 self-start text-xs font-semibold text-ink-3 underline"
          >
            {t('composerMedia.dismiss')}
          </button>
        </>
      ) : (
        <div
          className="mt-2 h-1.5 overflow-hidden rounded-full bg-border"
          role="progressbar"
          aria-valuenow={Math.round(u.progress * 100)}
          aria-label={t('composerMedia.uploading', { name: u.name })}
        >
          <div className="h-full bg-primary-600 transition-[width]" style={{ width: `${Math.max(4, u.progress * 100)}%` }} />
        </div>
      )}
    </li>
  ));

  return (
    <div className="flex flex-col gap-3">
      {items.length > 0 && !isCarousel ? (
        // One file (R16, R24): the thumbnail with its actions spelled out beside it, or under it
        // when a narrow phone has no room for "Identifier des personnes" beside a 128px thumbnail.
        <ul aria-label={t('composerMedia.list')}>
          {items.map((item) => (
            <li key={item.key} className="flex flex-wrap items-end gap-3">
              <div className={`relative shrink-0 overflow-hidden rounded-[10px] bg-surface-3 ring-1 ring-border ${type === 'story' ? 'h-32 w-[72px]' : 'size-32'}`}>
                <MediaThumb asset={item.asset} className="size-full" />
                {taggedBadge(item)}
              </div>
              <div className="flex flex-col items-start gap-1.5">
                {item.asset.kind === 'image' && canDescribe ? (
                  <Button size="sm" variant="secondary" icon={canTag ? 'tag' : 'pencil'} onClick={() => setEditing(item.key)}>
                    {t(canTag ? 'composerMedia.tag' : 'composerMedia.alt')}
                  </Button>
                ) : null}
                <Button size="sm" variant="danger-ghost" icon="trash" onClick={() => removeItem(item.key)}>
                  {t('composerMedia.remove')}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
      {uploads.length > 0 && !isCarousel ? <ul className={TILES}>{uploadTiles}</ul> : null}

      {hasMedia && isCarousel ? (
        <ul aria-label={t('composerMedia.list')} className={TILES}>
          {items.map((item, index) => (
            <li key={item.key} className="min-w-0">
              <div className="relative overflow-hidden rounded-xl bg-surface-3 ring-1 ring-border">
                <MediaThumb asset={item.asset} className="aspect-square w-full" />
                <span className={`${BADGE} left-2`}>{index + 1}</span>
                {taggedBadge(item)}
              </div>
              <div className="mt-1.5 flex items-center gap-0.5">
                {item.asset.kind === 'image' ? (
                  <button
                    type="button"
                    aria-label={t(canTag ? 'composerMedia.tag' : 'composerMedia.describe')}
                    onClick={() => setEditing(item.key)}
                    className="touch-target inline-flex h-8 items-center gap-1 whitespace-nowrap rounded-lg px-2 text-[12.5px] font-semibold text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink"
                  >
                    <Icon name={canTag ? 'tag' : 'pencil'} className="size-[14px]" />
                    {t(canTag ? 'composerMedia.tagShort' : 'composerMedia.altShort')}
                  </button>
                ) : null}
                <IconButton size="sm" variant="ghost" icon="arrowLeft" label={t('composerMedia.moveLeft', { n: index + 1 })} disabled={index === 0} onClick={() => move(index, -1)} />
                <IconButton
                  size="sm"
                  variant="ghost"
                  icon="arrowRight"
                  label={t('composerMedia.moveRight', { n: index + 1 })}
                  disabled={index === items.length - 1}
                  onClick={() => move(index, 1)}
                />
                <IconButton
                  size="sm"
                  variant="ghost"
                  icon="trash"
                  label={t('composerMedia.deleteItem', { n: index + 1 })}
                  onClick={() => removeItem(item.key)}
                  className="ml-auto hover:text-danger-700"
                />
              </div>
            </li>
          ))}
          {uploadTiles}
        </ul>
      ) : null}

      {canAdd ? (
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          onDragOver={(event) => {
            event.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={onDrop}
          className={`flex w-full flex-col items-center justify-center gap-1.5 rounded-ds-lg border-[1.5px] border-dashed p-[22px] text-center transition-colors [&>*]:pointer-events-none ${
            isDragging ? 'border-primary-600 bg-primary-50' : 'border-border-strong bg-surface-2 hover:border-primary-600'
          }`}
        >
          <span className="text-ds-base font-semibold text-ink">{hasMedia ? copy.more : copy.empty}</span>
          <span className="text-ds-sm text-ink-3">{copy.hint}</span>
          {/* The whole zone is the button; this only looks like one (DS dropzone). */}
          <span aria-hidden className={`${buttonClasses('secondary', 'sm')} mt-1`}>
            {t('composerMedia.chooseFile')}
          </span>
        </button>
      ) : (
        <p className="text-xs text-ink-3">{t('composerMedia.full', { max: POST_LIMITS.carouselMax })}</p>
      )}
      <input
        ref={fileInput}
        type="file"
        aria-label={t('composerMedia.addMedia')}
        accept={photosOnly(type, targets) ? ACCEPT.image : ACCEPT[type]}
        multiple={isCarousel}
        onChange={addFiles}
        className="hidden"
      />

      {notes.length > 0 ? (
        <ul className="space-y-0.5 text-xs text-ink-3">
          {notes.map((note) => (
            <li key={note}>• {note}</li>
          ))}
        </ul>
      ) : null}

      {type === 'reel' && targets.instagram ? (
        <div className="flex flex-wrap items-center gap-3 rounded-ds-md bg-surface-2 p-3">
          {cover ? <img src={cover.url} alt="" className="h-16 w-9 rounded-md object-cover" /> : null}
          <div className="min-w-[160px] flex-1">
            <p className="text-[13px] font-semibold text-ink">{t('composerMedia.cover.title')}</p>
            <p className="text-xs text-ink-3">{t(targets.facebook ? 'composerMedia.cover.hintBoth' : 'composerMedia.cover.hint')}</p>
          </div>
          {cover ? (
            <Button variant="ghost" size="sm" onClick={() => onCoverChange(null)}>
              {t('composerMedia.remove')}
            </Button>
          ) : null}
          <Button variant="secondary" size="sm" onClick={() => coverInput.current?.click()}>
            {t(cover ? 'composerMedia.cover.change' : 'composerMedia.cover.add')}
          </Button>
          <input ref={coverInput} type="file" aria-label={t('composerMedia.cover.input')} accept="image/*" onChange={addCover} className="hidden" />
        </div>
      ) : null}

      {editingItem ? (
        <Modal title={t(canTag ? 'composerMedia.modal.tag' : 'composerMedia.modal.describe')} onClose={() => setEditing(null)} wide={canTag}>
          {canTag ? (
            <TagPeopleEditor
              imageUrl={editingItem.asset.url}
              tags={editingItem.userTags}
              onChange={(userTags) => patchItem(editingItem.key, { userTags })}
            />
          ) : (
            <img src={editingItem.asset.url} alt="" className="max-h-64 w-full rounded-xl object-contain" />
          )}
          <div className="mt-4">
            <Field label={t('composerMedia.alt')} hint={t('composerMedia.modal.altHint')}>
              <Textarea
                rows={2}
                maxLength={POST_LIMITS.altTextMax}
                value={editingItem.altText}
                onChange={(event) => patchItem(editingItem.key, { altText: event.target.value })}
              />
            </Field>
          </div>
          <div className="mt-5 flex justify-end">
            <Button onClick={() => setEditing(null)}>{t('composerMedia.modal.done')}</Button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
